import { viewerStrings } from '../copy/strings.js';
import { message, CopyError } from '../copy/text.js';
import type { ColumnState, SelectionEventSourceType } from 'ag-grid-community';
import type {
  RelationshipField,
  RelationshipQueryRow,
  RelationshipQueryDefinition,
} from '@cdr-clarity/contracts';
import type { ViewPreference } from '../state/view-preferences.js';
import { unifiedRecordPage, type ExecuteRelationshipQuery } from '../state/unified-record-page.js';

export function tableSourceId(row: RelationshipQueryRow): string {
  return (row.primary ?? row.related!).id;
}

/** Only an explicit row-selection action changes source selection; refreshed nodes inherit it. */
export function changeTableSelection(
  current: ReadonlySet<string>,
  row: RelationshipQueryRow | undefined,
  selected: boolean | undefined,
  source: SelectionEventSourceType,
): ReadonlySet<string> {
  if (
    !row ||
    selected === undefined ||
    ![
      'checkboxSelected',
      'rowClicked',
      'spaceKey',
      'keyboardSelectAll',
      'uiSelectAll',
      'uiSelectAllFiltered',
      'uiSelectAllCurrentPage',
    ].includes(source)
  )
    return current;
  const id = tableSourceId(row);
  if (current.has(id) === selected) return current;
  const next = new Set(current);
  if (selected) next.add(id);
  else next.delete(id);
  return next;
}

/** Canonical legacy layout applies to both bindings; explicit qualified widths take precedence. */
export function tableColumnState(
  fields: readonly RelationshipField[],
  preference: Pick<ViewPreference, 'order' | 'widths' | 'sort' | 'pinned'>,
): ColumnState[] {
  const ids = [
    ...new Set([
      ...preference.order.flatMap((id) =>
        fields
          .filter((field) => field.id === id || field.sourceField === id)
          .map((field) => field.id),
      ),
      ...fields.map((field) => field.id),
    ]),
  ];
  const sorting = preference.sort.flatMap((sort) => {
    const field =
      fields.find((field) => field.id === sort.key) ??
      fields.find((field) => field.sourceField === sort.key && field.binding === 'primary') ??
      fields.find((field) => field.sourceField === sort.key);
    return field ? [{ ...sort, key: field.id }] : [];
  });
  return ids.map((colId) => {
    const field = fields.find((field) => field.id === colId)!;
    const width = preference.widths[colId] ?? preference.widths[field.sourceField];
    const pins = preference.pinned ?? {};
    const pinned = Object.hasOwn(pins, colId) ? pins[colId]! : (pins[field.sourceField] ?? null);
    const sortIndex = sorting.findIndex((sort) => sort.key === colId);
    return {
      colId,
      ...(width === undefined ? {} : { width }),
      pinned,
      sort: sortIndex < 0 ? null : sorting[sortIndex]!.direction,
      sortIndex: sortIndex < 0 ? null : sortIndex,
    };
  });
}

export interface PinnedAssociation {
  readonly membershipId: string;
  readonly sourceId: string;
}
export const maxPinnedRows = 20;
export function togglePinnedAssociation(
  pins: readonly PinnedAssociation[],
  row: RelationshipQueryRow,
): readonly PinnedAssociation[] {
  if (pins.some((pin) => pin.membershipId === row.membershipId))
    return pins.filter((pin) => pin.membershipId !== row.membershipId);
  if (pins.length >= maxPinnedRows)
    throw new CopyError(
      message(viewerStrings.webComponentsRecordTableState.freezeUpToRowsAtATime, {
        value1: maxPinnedRows,
      }),
    );
  return [...pins, { membershipId: row.membershipId, sourceId: (row.related ?? row.primary!).id }];
}

/** Reproject exact pinned associations with the active shared scope, filters and visible columns. */
export async function refreshPinnedAssociations(
  execute: ExecuteRelationshipQuery,
  definition: RelationshipQueryDefinition,
  columns: readonly string[],
  pins: readonly PinnedAssociation[],
): Promise<RelationshipQueryRow[]> {
  if (!pins.length) return [];
  const query = {
    ...definition,
    scope: {
      ...definition.scope,
      recordIds: definition.scope.recordIds ?? [...new Set(pins.map((pin) => pin.sourceId))],
    },
    order: [],
    page: { size: 500 },
  };
  const wanted = new Set(pins.map((pin) => pin.membershipId));
  const matched = new Map<string, RelationshipQueryRow>();
  let cursors: readonly string[] | null = null;
  for (let page = 0; page < 20; page++) {
    const result = await unifiedRecordPage(execute, query, columns, cursors);
    for (const row of result.result.rows)
      if (wanted.has(row.membershipId)) matched.set(row.membershipId, row);
    if (matched.size === wanted.size || !result.nextCursors)
      return pins.flatMap((pin) =>
        matched.has(pin.membershipId) ? [matched.get(pin.membershipId)!] : [],
      );
    cursors = result.nextCursors;
  }
  throw new CopyError(
    viewerStrings.webComponentsRecordTableState.frozenRowLookupExceedsSourceAssociationsNarrow,
  );
}
